import * as React from "react";
import {
  useForm,
  useFieldArray,
  useWatch,
  Control,
  UseFormHandleSubmit,
  FormState,
  UseFormSetValue,
  UseFormReset,
} from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  SegmentedControl,
  useMantineTheme,
  Divider,
  Flex,
  Stack,
  ActionIcon,
  Group,
  Tooltip,
  Modal,
  ScrollArea,
  Table,
  Text,
} from "@mantine/core";
import { yupResolver } from "@hookform/resolvers/yup";

import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { CTextarea, CTextInput } from "@supportinitiative/react-hook-form-mantine";
import { QuerySetNewSchema, QuerySetType } from "@supportinitiative/common";

import { apiAxios, getAuthHeader, getLocalStorage } from "../../api";
import { AuditType } from "../../types";
import { getActionOptions, SECTIONCOLORS } from "./QuerySetUtils";
import { UserContext } from "../../misc/UserContext";
import { useQuerySet, useQuerySetModifications, useUseCase } from "../../hooks";
import FormSubmitError from "../../ui/Error/FormSubmitError";
import FormStateError from "../../ui/Error/FormStateError";
import Success from "../../ui/Success";
import Audit from "../../ui/Audit/Audit";

import { styles } from "src/styles";
import { initialState, reducer, QuerySetCVMContext } from "./QuerySetCVMContext";
import PreferencesCVM from "./PreferencesCVM";
import {
  IconArrowBackUp,
  IconCancel,
  IconChevronLeft,
  IconCircleDashedCheck,
  IconCircleLetterX,
  IconCircleLetterXFilled,
  IconDeviceFloppy,
  IconEdit,
  IconPlus,
  IconSettingsCheck,
  IconTrash,
} from "@tabler/icons-react";
import { getId } from "src/utilities";
import QueryCVM from "./Query/QueryCVM";
import { getDefaultWorkflow } from "../../UseCase/Workflow/WorkflowUtils";
import Loading from "../../ui/Loading";
import dayjs from "dayjs";

async function postQuerySet(entityId: string, id: string | null | undefined, querySet: any) {
  return await apiAxios.post("/api/queryset", querySet, {
    params: {
      __entityId: entityId,
      __id: id,
    },
    headers: {
      Authorization: getAuthHeader(),
    },
  });
}

async function deleteQuerySet(entityId: string, id: string | null | undefined) {
  let response = await apiAxios.delete("/api/queryset", {
    params: {
      __entityId: entityId,
      __id: id,
    },
    headers: {
      Authorization: getAuthHeader(),
    },
  });
}

// TODO: Deletion

function Modifications({
  entityId,
  id,
  cancel,
}: {
  entityId: string | null | undefined;
  id: string | null | undefined;
  cancel: () => void;
}) {
  const { data: modifications } = useQuerySetModifications(entityId, id);

  return (
    <>
      <ScrollArea style={{ maxHeight: "75vh" }} offsetScrollbars scrollbarSize={16}>
        <Table verticalSpacing="sm" fs="xs" horizontalSpacing={"xs"} style={{ marginBottom: "20px" }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Version Hash</Table.Th>
              <Table.Th>Mod No</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Auth Status</Table.Th>
              <Table.Th>Usable Status</Table.Th>
              <Table.Th>Maker ID</Table.Th>
              <Table.Th>Maker Date</Table.Th>
              <Table.Th>Checker ID</Table.Th>
              <Table.Th>Checker Date</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {(modifications || []).map((mod) => {
              return (
                <Table.Tr key={mod.versionHash}>
                  <Table.Td>
                    <Link
                      to={`/app/querysets/edit?id=${id}&entityId=${entityId}&versionHash=${mod.versionHash}`}
                      //style={{ textDecoration: "none" }}
                      onClick={() => {
                        cancel();
                      }}
                    >
                      <Text size="sm">{mod.versionHash}</Text>
                    </Link>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{mod.modificationNo}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{mod.status}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{mod.authStatus}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{mod.usableStatus}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{mod.makerId}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{dayjs(mod.makerDate).format("YYYY-MM-DD HH:mm")}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{mod.checkerId}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{mod.checkerDate ? dayjs(mod.checkerDate).format("YYYY-MM-DD HH:mm") : ""}</Text>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </>
  );
}

function UserActions({ actions, querySet }: { actions: string[]; querySet: QuerySetType | undefined }) {
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId");
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { state: applicationState } = React.useContext(UserContext);

  const { state: querySetFormState, dispatch: querySetFormDispatch } = React.useContext(QuerySetCVMContext);

  const { data: querySetModifications } = useQuerySetModifications(entityId, id);

  // Modifications
  const [showModifications, setShowModifications] = React.useState(false);

  if (actions && actions.length > 0) {
    return (
      <>
        <Flex justify={"flex-start"} gap={"xs"}>
          {actions.map((a) => {
            if (a.toUpperCase() === "SAVE") {
              return (
                <Tooltip label="Save">
                  <ActionIcon variant="light" size="lg" type="submit">
                    <IconDeviceFloppy style={{ width: "70%", height: "70%" }} stroke={2} />
                  </ActionIcon>
                </Tooltip>
              );
            } else if (a.toUpperCase() === "CANCEL") {
              return (
                <Tooltip label="Cancel">
                  <ActionIcon variant="light" size="lg">
                    <IconArrowBackUp
                      style={{ width: "70%", height: "70%" }}
                      stroke={2}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/app/querysets?entityId=${entityId}`);
                      }}
                    />
                  </ActionIcon>
                </Tooltip>
              );
            } else if (a.toUpperCase() === "BACK") {
              return (
                <Tooltip label="Back">
                  <ActionIcon variant="light" size="lg">
                    <IconArrowBackUp
                      style={{ width: "70%", height: "70%" }}
                      stroke={2}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate(`/app/querysets?entityId=${entityId}`);
                      }}
                    />
                  </ActionIcon>
                </Tooltip>
              );
            } else if (a.toUpperCase() === "EDIT") {
              return (
                <Tooltip label="Edit">
                  <ActionIcon variant="light" size="lg">
                    <IconEdit
                      style={{ width: "70%", height: "70%" }}
                      stroke={2}
                      onClick={(event) => {
                        event.preventDefault();
                        querySetFormDispatch({
                          type: "CURRENT-ACTION",
                          value: "EDIT",
                        });

                        querySetFormDispatch({
                          type: "ACTION-OPTIONS",
                          value: getActionOptions("edit", querySet, querySet, applicationState),
                        });
                      }}
                    />
                  </ActionIcon>
                </Tooltip>
              );
            } else if (a.toUpperCase() === "MODIFICATIONS") {
              return (
                <Tooltip label="Modifications">
                  <ActionIcon variant="light" size="lg">
                    <IconCircleDashedCheck
                      style={{ width: "70%", height: "70%" }}
                      stroke={2}
                      onClick={(event) => {
                        event.preventDefault();
                        setShowModifications(true);
                      }}
                    />
                  </ActionIcon>
                </Tooltip>
              );
            } else if (a.toUpperCase() === "DELETE") {
              return (
                <Tooltip label="Delete">
                  <ActionIcon variant="light" size="lg">
                    <IconTrash
                      style={{ width: "70%", height: "70%" }}
                      stroke={2}
                      onClick={async (event) => {
                        event.preventDefault();
                        try {
                          querySetFormDispatch({
                            type: "SET-SUBMITTING",
                            value: true,
                          });
                          querySetFormDispatch({
                            type: "SET-ERROR",
                            value: "",
                          });

                          await deleteQuerySet(entityId as string, id);
                          querySetFormDispatch({
                            type: "SET-SUBMITTING",
                            value: false,
                          });
                          queryClient.invalidateQueries(["querySets", entityId]);
                          querySetFormDispatch({
                            type: "SET-SUCCESS",
                            value: "Query set deleted successfully",
                          });
                        } catch (error: any) {
                          querySetFormDispatch({
                            type: "SET-SUBMITTING",
                            value: false,
                          });
                          querySetFormDispatch({
                            type: "SET-ERROR",
                            value: error?.response?.data?.message || "Unable to delete query set",
                          });
                        }
                      }}
                    />
                  </ActionIcon>
                </Tooltip>
              );
            } else if (a.toUpperCase() === "AUTH") {
              return (
                <Tooltip label="Authorize">
                  <ActionIcon variant="light" size="lg">
                    <IconSettingsCheck
                      style={{ width: "70%", height: "70%" }}
                      stroke={2}
                      onClick={async () => {
                        querySetFormDispatch({
                          type: "CURRENT-ACTION",
                          value: "auth",
                        });
                        try {
                          let payload = {
                            ...querySet,
                            entityId: getLocalStorage("currentEntity"),
                            metadata: {
                              action: "auth",
                            },
                          };
                          await postQuerySet(entityId as string, id, payload);
                          // setError("Form", "Unable to Submit");
                          querySetFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "view",
                          });

                          querySetFormDispatch({
                            type: "SET-SUCCESS",
                            value: "Query set authorized successfully",
                          });
                        } catch (error: any) {
                          querySetFormDispatch({
                            type: "SET-SUBMITTING",
                            value: false,
                          });
                          querySetFormDispatch({
                            type: "SET-ERROR",
                            value: error?.response?.data?.message || "Unable to authorize query set",
                          });
                        }
                      }}
                    />
                  </ActionIcon>
                </Tooltip>
              );
            } else {
              return <></>;
            }
          })}
        </Flex>

        <Modal
          centered
          size={"100%"}
          opened={showModifications}
          onClose={() => setShowModifications(false)}
          title={
            <Title
              order={5}
              style={{
                padding: "0px 5px",
                backgroundColor: "#16A596",
                color: "#fff",
              }}
            >
              Modifications
            </Title>
          }
        >
          <Modifications entityId={entityId} id={id} cancel={() => setShowModifications(false)} />
        </Modal>
      </>
    );
  } else {
    return <></>;
  }
}

function QuerySetForm({
  entityId,
  querySet,
  action,
  control,
  handleSubmit,
  reset,
  setValue,
  formState,
  onSubmit,
}: {
  entityId: string;
  querySet: QuerySetType | undefined;
  action: string;
  control: Control<QuerySetType>;
  handleSubmit: UseFormHandleSubmit<QuerySetType>;
  reset: UseFormReset<QuerySetType>;
  setValue: UseFormSetValue<QuerySetType>;
  formState: FormState<QuerySetType>;
  onSubmit: any;
}) {
  // Initialize common function
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  // Set active tab to Preferences
  const [activeTab, setActiveTab] = React.useState<string>("0");

  const { state: querySetFormState, dispatch: querySetFormDispatch } = React.useContext(QuerySetCVMContext);

  const queriesArray = useFieldArray({
    control,
    name: "definition.queries",
    keyName: "__key",
  });

  const queries = useWatch({
    control,
    name: "definition.queries",
  });

  const onOk = () => {
    navigate(`/app/querySets?entityId=${entityId}`);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ marginTop: theme.spacing.lg }} noValidate>
      <div id="header">
        <Grid
          align="flex-end"
          gutter={"xs"}
          mt="xs"
          mb="xs"
          // mb="xs"
          style={{
            display: "flex",
            // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
          }}
          columns={12}
        >
          <Grid.Col span={2} mr="xs" style={{ borderRight: `2px solid ${theme.colors.dark[3]}` }}>
            <Flex align={"end"} gap={theme.spacing.xs}>
              <Stack gap={0}>
                <Title order={3} c="dark">
                  Queryset
                </Title>
              </Stack>
            </Flex>
          </Grid.Col>
          <Grid.Col span={9}>
            <UserActions actions={querySetFormState.actionOptions.allowedActions} querySet={querySet} />
          </Grid.Col>
        </Grid>
      </div>
      <Divider size="sm" mb="xs" color="dark.2" />

      <div id="body">
        <Stack
          gap="0"
          style={{ backgroundColor: SECTIONCOLORS[5], padding: theme.spacing.sm, borderRadius: theme.spacing.xxs }}
        >
          <Grid align="flex-start" gutter={"xs"} mb="xs" style={{ display: "flex", alignItems: "flex-end" }}>
            <Grid.Col span={2}>
              <CTextInput
                name="name"
                control={control}
                label="Name"
                required
                showErrorText={false}
                disabled={querySetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <CTextInput
                name="displayName"
                control={control}
                label="Display Name"
                required
                showErrorText={false}
                disabled={querySetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
          <Grid align="flex-start" gutter={"xs"} mb="xs" style={{ display: "flex", alignItems: "flex-end" }}>
            <Grid.Col span={8}>
              <CTextarea
                name="description"
                control={control}
                label="Description"
                autosize
                minRows={2}
                maxRows={20}
                required
                showErrorText={false}
                disabled={querySetFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>
        </Stack>
        <Stack
          gap="0"
          mb="lg"
          mt="xs"
          style={{ backgroundColor: SECTIONCOLORS[5], padding: theme.spacing.sm, borderRadius: theme.spacing.xxs }}
        >
          <Group mb="xs" gap={"xs"} style={{ alignItems: "center" }}>
            <SegmentedControl
              value={activeTab}
              onChange={setActiveTab}
              data={[
                {
                  label: `Preferences`,
                  value: "0",
                },
                /*                 {
                  label: `Trigger Flows`,
                  value: "3",
                }, */
                ...queries.map((query, index) => {
                  return {
                    label: `${query.name ? `${query.name} (${query.id})` : `Query ${index + 1}`}`,
                    value: (index + 1).toString(),
                  };
                }),
              ]}
            />
            {querySetFormState.currentAction.toUpperCase() !== "VIEW" && (
              <ActionIcon
                size="sm"
                variant="transparent"
                color="gray"
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.preventDefault();
                  const id = getId();
                  queriesArray.append({
                    id: id,
                    name: `Query set ${queriesArray.fields.length + 1}`,
                    description: "",
                    command: "",
                    inputs: [],
                    inputRules: [],
                    definition: {
                      preferences: {
                        run: "SYNC",
                      },
                      datasets: [],
                      workflow: getDefaultWorkflow(),
                    },
                  });
                  setActiveTab((queriesArray.fields.length + 1).toString());
                }}
                disabled={querySetFormState.actionOptions.disableAll}
              >
                <IconPlus stroke={2} />
              </ActionIcon>
            )}
          </Group>

          {activeTab === "0" ? (
            <PreferencesCVM name="definition" control={control} />
          ) : (
            <>
              {queriesArray.fields.map((field, index) => {
                if ((index + 1).toString() === activeTab) {
                  return (
                    <QueryCVM
                      id={field.id}
                      name={`definition.queries.${index}`}
                      control={control}
                      key={field.__key}
                      index={index}
                      queriesArray={queriesArray}
                      setActiveTab={setActiveTab}
                      fromWorkflowName={`definition.queries.${index}.definition`}
                    />
                  );
                }
              })}
            </>
          )}
        </Stack>
      </div>

      {(Object.keys(formState.errors).length > 0 || querySetFormState.error) && (
        <div style={styles.errors} id="errors">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <FormStateError errors={formState.errors} />
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            <FormSubmitError error={querySetFormState.error} />
          </div>
        </div>
      )}

      {querySetFormState.successMessage && <Success message={querySetFormState.successMessage} onOk={onOk} />}
    </form>
  );
}

function QuerySetCreateForm() {
  // Initialize common function
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  // Get search params
  let entityId = searchParams.get("entityId");

  // Initialize user context
  const { state: applicationState } = React.useContext(UserContext);

  // Initialize form controls
  const { control, handleSubmit, reset, setValue, formState } = useForm<QuerySetType>({
    defaultValues: {
      name: "",
      displayName: "",
      description: "",
      type: "QUERYSET",
      definition: {
        preferences: {
          command: "",
        },
        loaderId: "",
        queries: [],
      },
    },
    criteriaMode: "all",
    // resolver: yupResolver(querySetSchema),
    resolver: yupResolver(QuerySetNewSchema),
  });

  const [querySetFormState, querySetFormDispatch] = React.useReducer(reducer, {
    ...initialState,
    entityId,
    actionOptions: getActionOptions("NEW", undefined, undefined, applicationState),
    setValue: setValue,
    reset: reset,
  });

  const onSubmit = async (data: any) => {
    try {
      querySetFormDispatch({
        type: "SET-SUBMITTING",
        value: true,
      });
      querySetFormDispatch({
        type: "SET-ERROR",
        value: "",
      });
      let payload = {
        ...data,
        entityId,
        metadata: {
          action: querySetFormState.currentAction,
        },
      };
      await postQuerySet(entityId as string, undefined, payload);
      querySetFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      queryClient.invalidateQueries(["querySets", entityId]);
      querySetFormDispatch({
        type: "SET-SUCCESS",
        value: "Query set saved successfully",
      });
    } catch (error: any) {
      querySetFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      querySetFormDispatch({
        type: "SET-ERROR",
        value: error?.response?.data?.message || "Unable to save query set",
      });
    }
  };

  return (
    <QuerySetCVMContext.Provider value={{ state: querySetFormState, dispatch: querySetFormDispatch }}>
      <QuerySetForm
        action={"NEW"}
        entityId={entityId as string}
        querySet={undefined}
        control={control}
        reset={reset}
        handleSubmit={handleSubmit}
        setValue={setValue}
        formState={formState}
        onSubmit={onSubmit}
      />
    </QuerySetCVMContext.Provider>
  );
}

function QuerySetEditForm(props: { querySet: QuerySetType }) {
  // Initialize common function
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  // Get search params
  let entityId = searchParams.get("entityId");

  // Initialize user context
  const { state: applicationState } = React.useContext(UserContext);

  // Initialize form controls
  const { control, handleSubmit, reset, setValue, formState } = useForm<QuerySetType>({
    defaultValues: {
      name: "",
      displayName: "",
      description: "",
      type: "QUERYSET",
      definition: {
        preferences: {
          command: "",
        },
        loaderId: "",
        queries: [],
      },
    },
    values: props.querySet,
    criteriaMode: "all",
    // resolver: yupResolver(querySetSchema),
    resolver: yupResolver(QuerySetNewSchema),
  });

  const [querySetFormState, querySetFormDispatch] = React.useReducer(reducer, {
    ...initialState,
    entityId,
    currentAction: "VIEW",
    actionOptions: getActionOptions("VIEW", undefined, props.querySet, applicationState),
    setValue: setValue,
    reset: reset,
  });

  const onSubmit = async (data: any) => {
    try {
      querySetFormDispatch({
        type: "SET-SUBMITTING",
        value: true,
      });
      querySetFormDispatch({
        type: "SET-ERROR",
        value: "",
      });
      let payload = {
        ...data,
        entityId,
        metadata: {
          action: querySetFormState.currentAction,
        },
      };
      await postQuerySet(entityId as string, props.querySet.id, payload);
      querySetFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      queryClient.invalidateQueries(["querySets", entityId]);
      querySetFormDispatch({
        type: "SET-SUCCESS",
        value: "Query set saved successfully",
      });
    } catch (error: any) {
      querySetFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      querySetFormDispatch({
        type: "SET-ERROR",
        value: error?.response?.data?.message || "Unable to save query set",
      });
    }
  };

  return (
    <QuerySetCVMContext.Provider value={{ state: querySetFormState, dispatch: querySetFormDispatch }}>
      <QuerySetForm
        action={"EDIT"}
        entityId={entityId as string}
        querySet={props.querySet}
        control={control}
        reset={reset}
        handleSubmit={handleSubmit}
        setValue={setValue}
        formState={formState}
        onSubmit={onSubmit}
      />
    </QuerySetCVMContext.Provider>
  );
}

export default function QuerySetCVM() {
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId"),
    versionHash = searchParams.get("versionHash");

  const { data: querySet } = useQuerySet(entityId, id, versionHash);

  if (id) {
    if (querySet) {
      return <QuerySetEditForm querySet={querySet} />;
    } else {
      <Loading />;
    }
  } else {
    return <QuerySetCreateForm />;
  }

  return <></>;
}
